// =============================================================================
// RoleManagementPage  (scaffold — modal wired in Step 8)
//
// This page is rendered when the user taps the "Roles Management" card in
// AdminDashboard. It follows the same thin-page pattern as DLO pages:
//   - Custom hooks supply data and handlers
//   - Page owns the modal state via useState
//   - Child components receive only what they need via props
// =============================================================================

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from 'react-native';
import Svg, { Line, Path } from 'react-native-svg';
import FontFamily from '../../../../constants/fonts';
import { useToast } from '../../../../components/LhToastContainer';
import { useAppDispatch } from '../../../../store';
import { FetchRolesParams, fetchRoles } from '../../../../store/thunks/adminThunks';

import RoleTable from './components/RoleTable';
import RoleFormModal from './components/RoleFormModal';
import { useRoleManagementData } from './hooks/useRoleManagementData';
import { useRoleManagementHandlers } from './hooks/useRoleManagementHandlers';
import {
  RoleManagementPageProps,
  RoleModalState,
  INITIAL_MODAL_STATE,
} from './types';

// =============================================================================
// SMALL ATOMS
// =============================================================================

/** Chevron separator matching DLOBreadcrumb */
const BreadcrumbChevron = () => (
  <Svg width={16} height={16} viewBox="0 0 16 16" fill="none" style={{ marginHorizontal: 4 }}>
    <Path
      d="M4.61751 3.87988L8.68862 7.95988L4.61751 12.0399L5.87084 13.2932L11.2042 7.95988L5.87084 2.62655L4.61751 3.87988Z"
      fill="#616161"
    />
  </Svg>
);

/** Breadcrumb row: "Configuration Module  >  Roles Management" */
const Breadcrumb = ({ onBack }: { onBack: () => void }) => (
  <View style={styles.breadcrumb}>
    <TouchableOpacity onPress={onBack} activeOpacity={0.7}>
      <Text style={styles.breadcrumbLink}>Configuration Module</Text>
    </TouchableOpacity>
    <BreadcrumbChevron />
    <Text style={styles.breadcrumbCurrent}>Roles Management</Text>
  </View>
);

/** "+ Add Role" primary button */
const AddRoleButton = ({ onPress }: { onPress: () => void }) => (
  <TouchableOpacity onPress={onPress} activeOpacity={0.85} style={styles.addBtn}>
    <Svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth={2}>
      <Line x1="12" y1="5" x2="12" y2="19" />
      <Line x1="5" y1="12" x2="19" y2="12" />
    </Svg>
    <Text style={styles.addBtnText}>Add Role</Text>
  </TouchableOpacity>
);

// =============================================================================
// PAGE
// =============================================================================

const RoleManagementPage: React.FC<RoleManagementPageProps> = ({ onBack }) => {
  const defaultQuery: FetchRolesParams = {
    page: 0,
    size: 9999,
    sortBy: 'roleName',
    sortDirection: 'asc',
  };

  // ── Data ───────────────────────────────────────────────────────────────────
  const toast = useToast();
  const pageDispatch = useAppDispatch();
  const { roles, isLoading, isSaving, error, pagination, sort, filterOptions, dispatch } = useRoleManagementData();

  const [modalState, setModalState] = useState<RoleModalState>(INITIAL_MODAL_STATE);
  const [activeQuery, setActiveQuery] = useState<FetchRolesParams>(defaultQuery);

  const {
    openAddModal,
    openEditModal,
    handleToggleStatus,
    handleSaveRole,
  } = useRoleManagementHandlers({ dispatch, modalState, setModalState });

  // Show API-level fetch error as toast
  useEffect(() => {
    if (error) {
      toast.showToast({ header: 'Failed to load roles', subtext: error, variant: 'error' });
    }
  }, [error, toast]);

  // ── Filtering handlers ──────────────────────────────────────────────────────
  const handleApplyFilters = (params: any) => {
    // Build a clean query from only the present filter keys —
    // do NOT spread activeQuery, which would preserve stale filters.
    const nextQuery: FetchRolesParams = {
      page: 0,
      size: 9999,
      sortBy: params?.sortBy ?? activeQuery.sortBy ?? 'roleName',
      sortDirection: params?.sortDirection ?? activeQuery.sortDirection ?? 'asc',
    };
    if (params?.roleName) nextQuery.roleName = params.roleName;
    if (params?.availableIns) nextQuery.availableIns = params.availableIns;
    if (params?.locationTypes) nextQuery.locationTypes = params.locationTypes;
    if (params?.statuses) nextQuery.statuses = params.statuses;
    if (params?.organizationTypes) nextQuery.organizationTypes = params.organizationTypes;
    if (params?.threePlStatuses) nextQuery.threePlStatuses = params.threePlStatuses;
    if (params?.roleTypes) nextQuery.roleTypes = params.roleTypes;

    setActiveQuery(nextQuery);
    pageDispatch(fetchRoles(nextQuery));
  };

  const handleSortChange = (sortBy: 'roleName' | 'modifiedAt', sortDirection: 'asc' | 'desc') => {
    const nextQuery: FetchRolesParams = {
      ...activeQuery,
      page: 0,
      size: 9999,
      sortBy,
      sortDirection,
    };

    setActiveQuery(nextQuery);
    pageDispatch(fetchRoles(nextQuery));
  };

  const handleReset = () => {
    setActiveQuery(defaultQuery);
    pageDispatch(fetchRoles(defaultQuery));
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps="handled"
    >
      {/* ── Breadcrumb ── */}
      <Breadcrumb onBack={onBack} />

      {/* ── Page header ── */}
      <View style={styles.pageHeader}>
        <View>
          <Text style={styles.pageTitle}>Roles</Text>
          <Text style={styles.pageSubtitle}>
            Manage warehouse roles for Observations and Labor Optimization
          </Text>
        </View>
        <AddRoleButton onPress={openAddModal} />
      </View>

      {/* ── Roles table ── */}
      <RoleTable
        roles={roles}
        isLoading={isLoading}
        pagination={pagination}
        sort={sort}
        filterOptions={filterOptions}
        onRowClick={openEditModal}
        onApplyFilters={handleApplyFilters}
        onSortChange={handleSortChange}
        onReset={handleReset}
      />

      {/* ── Role Add/Edit Modal ── */}
      <RoleFormModal
        visible={modalState.formVisible}
        mode={modalState.formMode}
        initialValues={modalState.editingRole}
        isSaving={isSaving}
        onSave={handleSaveRole}
        onCancel={() => setModalState(INITIAL_MODAL_STATE)}
        existingRoles={roles}
      />
    </ScrollView>
  );
};

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#EEF2F8',
  },
  content: {
    padding: 32,
    paddingBottom: 60,
  },

  // ── Breadcrumb ─────────────────────────────────────────────────────────────
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#005CBC',
  },

  breadcrumbCurrent: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#2D2D2D',
  },

  // ── Page header ────────────────────────────────────────────────────────────
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 28,
    fontFamily: FontFamily.bold,
    color: '#2D2D2D',
    lineHeight: 40,
  },
  pageSubtitle: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#616161',
    marginTop: 4,
  },

  // ── Add Role button ────────────────────────────────────────────────────────
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 40,
    paddingHorizontal: 24,
    borderRadius: 24,
    backgroundColor: '#034895',
  },
  addBtnText: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: '#FFFFFF',
  },

});

export default RoleManagementPage;
